import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheck } from '#src/atoms/icons/system/iconCheck.ts';
import { iconCopy } from '#src/atoms/icons/system/iconCopy.ts';
import { iconDownload } from '#src/atoms/icons/system/iconDownload.ts';
import { Actions } from '#src/atoms/layout/Actions/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineS } from '#src/atoms/typography/index.tsx';
import { useCopyToClipboard } from '#src/hooks/useCopyToClipboard.ts';
import { glossary } from '#src/lib/glossary.ts';

type Props = {
  onClose: () => void;
  codes: string[];
  onSavedChange?: (hasSaved: boolean) => void;
};

const messages = defineMessages({
  subtitle: {
    defaultMessage: 'Please save those backup codes!',
  },
  description: {
    defaultMessage:
      'Those codes will allow you to enter your account if you lose your auth app. Each code can only be used once! Any previously generated codes no longer work.',
  },
  saveToContinue: {
    defaultMessage: 'Copy or download your codes to continue.',
  },
  storeSecurely: {
    defaultMessage:
      'Keep these somewhere secure and synced, like a password manager — not on the device you use to generate your authenticator codes.',
  },
});

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
  width: 100%;
  max-width: 480px;
`;

const CodeBox = styled.div`
  width: 100%;
  background-color: var(--c-nd-100);
  padding: 0 var(--double-unit);
`;
const CodeList = styled.ul`
  columns: 2;
  padding-left: 0;
  list-style-type: none;
`;

const downloadCodes = (codes: string[]) => {
  const blob = new Blob([`${codes.join('\n')}\n`], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'sorare-recovery-codes.txt';
  anchor.click();
  URL.revokeObjectURL(url);
};

export const RecoveryCodesDialogContent = ({
  codes,
  onClose,
  onSavedChange,
}: Props) => {
  const copyToClipboard = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  const markSaved = () => {
    setHasSaved(true);
    onSavedChange?.(true);
  };

  const handleCopy = () => {
    copyToClipboard(codes.join('\n'), () => {
      setCopied(true);
      markSaved();
    });
  };

  const handleDownload = () => {
    downloadCodes(codes);
    markSaved();
  };

  return (
    <Wrapper>
      <HeadlineS as="h5" color="var(--c-white)">
        <FormattedMessage {...messages.subtitle} />
      </HeadlineS>
      <BodyM color="var(--c-nd-600)">
        <FormattedMessage {...messages.description} />
      </BodyM>
      <CodeBox>
        <CodeList>
          {codes.map(code => (
            <li key={code}>
              <code>{code}</code>
            </li>
          ))}
        </CodeList>
      </CodeBox>
      <Actions>
        <Button
          color="tertiary"
          size="small"
          onClick={handleCopy}
          startIcon={<FontAwesomeIcon icon={copied ? iconCheck : iconCopy} />}
        >
          {copied ? (
            <FormattedMessage {...glossary.copiedToClipboard} />
          ) : (
            <FormattedMessage {...glossary.copy} />
          )}
        </Button>
        <Button
          color="tertiary"
          size="small"
          onClick={handleDownload}
          startIcon={<FontAwesomeIcon icon={iconDownload} />}
        >
          <FormattedMessage {...glossary.download} />
        </Button>
      </Actions>
      <BodyM color="var(--c-nd-600)">
        {hasSaved ? (
          <FormattedMessage {...messages.storeSecurely} />
        ) : (
          <FormattedMessage {...messages.saveToContinue} />
        )}
      </BodyM>
      <Button
        color="primary"
        size="small"
        disabled={!hasSaved}
        onClick={() => onClose()}
      >
        <FormattedMessage {...glossary.done} />
      </Button>
    </Wrapper>
  );
};
